import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import MainLayout from '../../components/layout/MainLayout';
import AlertsTable from './components/AlertsTable';
import AlertsSearchFilters from './components/AlertsSearchFilters';
import CandidatesPagination from '../candidates-list/components/CandidatesPagination';
import { useAuth } from '../../contexts/AuthContext';
import { getAlerts } from '../../lib/api';
import { transformAlert, getAlertTypeFilterLabels } from '../../lib/utils/alertLabels';
import { filterAlerts } from '../../lib/utils/filterAlerts';

const PAGE_SIZE = 50;

const formatFilterDate = (value) => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'dd-MM-yyyy');
  } catch {
    return value;
  }
};

const AlertsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [alertType, setAlertType] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);

  const alertTypeLabels = useMemo(() => getAlertTypeFilterLabels(), [i18n.language]);

  const hasActiveFilters = Boolean(
    candidateSearch.trim() || dateFilter || alertType !== 'all',
  );

  const transformedAlerts = useMemo(
    () => (alerts || []).map(transformAlert),
    [alerts, i18n.language],
  );

  const filteredAlerts = useMemo(
    () => filterAlerts(transformedAlerts, { candidateSearch, dateFilter, alertType }),
    [transformedAlerts, candidateSearch, dateFilter, alertType],
  );

  const totalCount = filteredAlerts.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(currentPage, pageCount - 1);

  const paginatedAlerts = useMemo(() => {
    const start = safePage * PAGE_SIZE;
    return filteredAlerts.slice(start, start + PAGE_SIZE);
  }, [filteredAlerts, safePage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [candidateSearch, dateFilter, alertType]);

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  const clearAllFilters = () => {
    setCandidateSearch('');
    setDateFilter('');
    setAlertType('all');
  };

  useEffect(() => {
    const fetchAlerts = async () => {
      if (!userId) {
        setLoading(false);
        hasLoadedRef.current = false;
        return;
      }

      const useSilent = hasLoadedRef.current;

      try {
        if (!useSilent) setLoading(true);
        const data = await getAlerts(userId);
        setAlerts(data || []);
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        if (!useSilent) setLoading(false);
      }
    };

    fetchAlerts();
  }, [userId]);

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
            {t('alerts.title')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('alerts.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            <AlertsSearchFilters
              candidateSearch={candidateSearch}
              onCandidateSearchChange={setCandidateSearch}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              alertType={alertType}
              onAlertTypeChange={setAlertType}
              totalAlerts={totalCount}
            />

            {hasActiveFilters && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('alerts.activeFilters')}</span>
                {candidateSearch.trim() && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                    {t('alerts.filterSearch', { term: candidateSearch })}
                    <button
                      type="button"
                      onClick={() => setCandidateSearch('')}
                      className="hover:bg-blue-100 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {dateFilter && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full">
                    {t('alerts.filterDateChip', { date: formatFilterDate(dateFilter) })}
                    <button
                      type="button"
                      onClick={() => setDateFilter('')}
                      className="hover:bg-purple-100 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {alertType !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">
                    {t('alerts.filterTypeChip', { label: alertTypeLabels[alertType] || alertType })}
                    <button
                      type="button"
                      onClick={() => setAlertType('all')}
                      className="hover:bg-teal-100 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  {t('common.clearAll')}
                </button>
              </div>
            )}

            {!loading && totalCount > 0 && totalCount <= PAGE_SIZE && (
              <div className="mb-4 text-sm text-muted-foreground">
                {t('alerts.showing')}{' '}
                <span className="font-semibold text-foreground">{totalCount}</span>{' '}
                {totalCount === 1 ? t('common.alert') : t('common.alerts_word')}
                {hasActiveFilters && t('alerts.matchingFilters')}
              </div>
            )}

            {!loading && totalCount === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <div className="text-muted-foreground mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{t('alerts.emptyTitle')}</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {hasActiveFilters ? t('alerts.emptyFiltered') : t('alerts.emptyDefault')}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-sm text-primary hover:text-accent underline"
                  >
                    {t('common.clearAll')}
                  </button>
                )}
              </div>
            ) : (
              !loading && (
                <>
                  <AlertsTable activities={paginatedAlerts} />
                  <CandidatesPagination
                    currentPage={safePage}
                    pageCount={pageCount}
                    pageSize={PAGE_SIZE}
                    totalCount={totalCount}
                    onPageChange={setCurrentPage}
                    itemLabel="alert"
                  />
                </>
              )
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default AlertsPage;
